import { useSelector } from 'utils/hooks'
import { useCallback, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import i18n from 'translations/i18n'
import { payrunActivationStatus } from 'redux/payroll/payrun/actionsCreator'

// eslint-disable-next-line no-shadow
export enum ModuleStatus {
  Pending = 'pending',
  Completed = 'completed',
  NotStarted = 'notStarted',
}

export type ModuleType = {
  id: string
  icon: string
  title: string
  subTitle: string
  path: string
  status: ModuleStatus
  completionPath: string
  completionLabel: string
}

const getLeavesModuleStatus = (steps?: { completed: boolean }[]): ModuleStatus => {
  if (!steps) return ModuleStatus.NotStarted

  const leavesModuleComplete = steps.every((step) => step.completed)
  if (leavesModuleComplete) return ModuleStatus.Completed // All steps completed

  const leavesModuleStarted = steps.some((step) => step.completed)
  if (leavesModuleStarted) return ModuleStatus.Pending // At least one step is completed

  return ModuleStatus.NotStarted
}

export const useModulesList = (): ModuleType[] => {
  const { page_groups: pageGroups } = useSelector((state) => state.onboardingSteps)
  const { onboardingSteps } = useSelector(({ attendanceDashboard }) => attendanceDashboard)
  const payrunActivationStatusState = useSelector(
    ({ payrun }) => payrun.payrun_activation_status,
    shallowEqual,
  )
  const employee = useSelector(({ auth }) => auth.employee, shallowEqual)
  const onboardingCompleted = employee?.organization.attendance_onboarding_completed

  const getAttendanceModuleCompleted = useCallback((): ModuleStatus => {
    if (onboardingCompleted) return ModuleStatus.Completed
    return onboardingSteps.length > 0 ? ModuleStatus.Pending : ModuleStatus.NotStarted
  }, [onboardingCompleted, onboardingSteps])

  const getPayrollModuleStatus = useCallback((): ModuleStatus => {
    if (payrunActivationStatusState && payrunActivationStatusState.status === 'completed') {
      return ModuleStatus.Completed
    } else if (payrunActivationStatusState && payrunActivationStatusState.status === 'pending') {
      return ModuleStatus.Pending
    }
    return ModuleStatus.NotStarted
  }, [payrunActivationStatus])

  const steps = pageGroups.find(
    (element) => element.name === 'onboarding_leaves',
  )?.organization_steps

  const isLastStepCompleted = steps?.find((e) => e.name === 'onboarding_leaves_review')?.completed
  const moduleStatus = useMemo(() => {
    if (isLastStepCompleted) {
      return ModuleStatus.Completed
    }
    return getLeavesModuleStatus(steps)
  }, [isLastStepCompleted, steps])

  return useMemo(
    () => [
      {
        id: 'leaves',
        icon: 'clock',
        title: 'leaves',
        subTitle: i18n.t(
          'description.define_the_vacation_balances_and_allow_your_employees_to_request_leaves.',
        ),
        path: '/new_onboarding/module_setup/leaves',
        status: moduleStatus,
        completionPath: '/leave_management',
        completionLabel: 'label.go_to_leaves',
      },
      {
        id: 'attendance',
        icon: 'lightning-01',
        title: 'attendance',
        subTitle: i18n.t(
          'description.manage_your_employees_punch_in_and_punch_out_and_schedule_their_shifts.',
        ),
        path: '/dashboard',
        status: getAttendanceModuleCompleted(),
        completionPath: '/attendance/tracking',
        completionLabel: 'label.go_to_attendance',
      },
      {
        id: 'payroll',
        icon: 'coins-stacked-01',
        title: 'payroll_new_onboarding',
        subTitle: i18n.t('description.manage_review_and_run_your_employees_payroll.'),
        path: '/payroll',
        status: getPayrollModuleStatus(),
        completionPath: '/payroll',
        completionLabel: 'label.go_to_payroll',
      },
    ],
    [steps, onboardingSteps.length, onboardingCompleted, payrunActivationStatusState],
  )
}
